import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import type { ApiError } from '@/shared/lib/problem-details';
import { getAmendmentRequests } from '../api/orders.api';
import type { AmendmentRequestListItem, AmendmentRequestListResponse } from '../types/orders.types';
import { recordServerTime, serverClockNow } from '../utils/server-clock';
import { AFTER_DEADLINE_MS, CANCELLATION_REQUESTS_REFETCH_MS } from './useCancellationRequests';
import { ordersKeys } from './useOrders';

/* ==========================================================================
   Lista de alterações que esperam a loja (change orders-customer-amendments,
   decisão 10): o chip "N solicitações" e o alerta global somam esta lista à
   das solicitações de cancelamento.

   Releitura: pelos eventos `order.amendment_requested`, `order.amended` e
   `order.amendment_rejected` e na reconexão (a chave fica fora da raiz dos
   pedidos, como a do cancelamento), e a cada 30 segundos enquanto a lista não
   estiver vazia; com uma esperando, a próxima leitura também vem logo depois
   do prazo dela, quando a varredura do servidor já a expirou. A lista só traz
   as que estão no prazo: a vencida sai dela, e não vira outro estado.

   A rota que não responde (a API anterior, com 404) não é falha de tela: o
   chip e o alerta contam só os cancelamentos.
   ========================================================================== */

/** O intervalo da próxima releitura, ou `false` com a lista vazia. */
export function amendmentRequestsRefetchInterval(
  data: AmendmentRequestListResponse | undefined,
  now = serverClockNow(),
): number | false {
  if (!data || data.items.length === 0) return false;
  const deadlines = data.items
    .map((item) => Date.parse(item.deadline) - now)
    .filter((ms) => Number.isFinite(ms) && ms >= 0);
  if (deadlines.length === 0) return CANCELLATION_REQUESTS_REFETCH_MS;
  return Math.min(CANCELLATION_REQUESTS_REFETCH_MS, Math.min(...deadlines) + AFTER_DEADLINE_MS);
}

export interface UseAmendmentRequestsOptions {
  /** Falso: não pergunta (o bloco sem alteração pendente na tela). */
  enabled?: boolean;
}

export function useAmendmentRequests({ enabled = true }: UseAmendmentRequestsOptions = {}) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<AmendmentRequestListResponse, ApiError>({
    queryKey: ordersKeys.amendmentRequests(tenantId),
    queryFn: async () => {
      const data = await getAmendmentRequests();
      recordServerTime(data.serverTime);
      return data;
    },
    enabled: enabled && !!tenantId,
    refetchInterval: (query) => amendmentRequestsRefetchInterval(query.state.data),
    // O 404 da API anterior e o 403 de um token antigo: o chip e o alerta
    // ficam só com os cancelamentos, sem nova tentativa.
    retry: false,
  });
}

/** As que ainda esperam no relógio do servidor: a de prazo passado sai sem esperar a próxima leitura. */
export function waitingAmendments(
  items: readonly AmendmentRequestListItem[] | undefined,
  now: number,
): AmendmentRequestListItem[] {
  return (items ?? []).filter((item) => {
    const deadline = Date.parse(item.deadline);
    return !Number.isFinite(deadline) || deadline > now;
  });
}
